// Proper SQL text parsing implementation
export interface PollData {
  pollUniqueId: number;
  pollWeek: number;
  pollYear: number;
  pollDate: string;
  pollRank: number;
  pollPrev: number | null;
  pollSchool: string;
  pollFpv: number | null;
  pollPoints: number | null;
  pollTeams: number;
  pollStatus: string;
  pollAtp: number | null;
}

export interface SeasonWeek {
  year: number;
  week: number;
  date: string;
}

class DatabaseService {
  private pollData: PollData[] = [];
  private isInitialized = false;

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Fetch and parse the SQL file
      const response = await fetch('/BasketBase.sql');
      const sqlText = await response.text();
      
      this.pollData = this.parseSqlData(sqlText);
      this.isInitialized = true;
      console.log(`Parsed ${this.pollData.length} poll records successfully`);
    } catch (error) {
      console.error('Failed to initialize database:', error);
      throw error;
    }
  }

  private parseSqlData(sqlText: string): PollData[] {
    const allPollData: PollData[] = [];
    
    // Find ALL INSERT INTO appoll statements
    const insertMatches = sqlText.match(/INSERT INTO `appoll` VALUES \((.*?)\);/gs);
    if (!insertMatches || insertMatches.length === 0) {
      console.error('Could not find any appoll INSERT statements');
      return [];
    }


    insertMatches.forEach((insertMatch, insertIndex) => {
      const valuesString = insertMatch.match(/INSERT INTO `appoll` VALUES \((.*?)\);/s)?.[1];
      if (!valuesString) return;

      // Split by ),( to get individual records
      const records = valuesString.split('),(');
      
      records.forEach((record, index) => {
        try {
          // Clean up the record string
          let cleanRecord = record.trim();
          if (index === 0) cleanRecord = cleanRecord.substring(1); // Remove leading (
          if (index === records.length - 1) cleanRecord = cleanRecord.substring(0, cleanRecord.length - 1); // Remove trailing )
          
          // Parse the record fields
          const fields = this.parseRecordFields(cleanRecord);
          
          if (fields.length >= 12) {
            const pollData: PollData = {
              pollUniqueId: parseInt(fields[0]) || 0,
              pollWeek: parseInt(fields[1]) || 0,
              pollYear: parseInt(fields[2]) || 0,
              pollDate: fields[3]?.replace(/'/g, '') || '',
              pollRank: parseInt(fields[4]) || 0,
              pollPrev: fields[5] === 'NULL' ? null : parseInt(fields[5]) || null,
              pollSchool: fields[6]?.replace(/'/g, '') || '',
              pollFpv: fields[7] === 'NULL' ? null : parseInt(fields[7]) || null,
              pollPoints: fields[8] === 'NULL' ? null : parseInt(fields[8]) || null,
              pollTeams: parseInt(fields[9]) || 0,
              pollStatus: fields[10]?.replace(/'/g, '') || '',
              pollAtp: fields[11] === 'NULL' ? null : parseInt(fields[11]) || null,
            };
            
            allPollData.push(pollData);
          }
        } catch (error) {
          // Skip malformed records
        }
      });
    });

    
    return allPollData;
  }

  private parseRecordFields(record: string): string[] {
    const fields: string[] = [];
    let currentField = '';
    let inQuotes = false;
    let quoteChar = '';
    let i = 0;
    
    while (i < record.length) {
      const char = record[i];
      
      if (!inQuotes && (char === "'" || char === '"')) {
        inQuotes = true;
        quoteChar = char;
        currentField += char;
      } else if (inQuotes && char === quoteChar) {
        // Check for escaped quotes
        if (i + 1 < record.length && record[i + 1] === quoteChar) {
          currentField += char + char;
          i++; // Skip the next quote
        } else {
          inQuotes = false;
          quoteChar = '';
          currentField += char;
        }
      } else if (!inQuotes && char === ',') {
        fields.push(currentField.trim());
        currentField = '';
      } else {
        currentField += char;
      }
      i++;
    }
    
    // Add the last field
    if (currentField.trim()) {
      fields.push(currentField.trim());
    }
    
    return fields;
  }

  async getAvailableSeasons(): Promise<SeasonWeek[]> {
    try {
      // Use the API to get available seasons
      const response = await fetch('https://basketbase-workers.bakonydraco1.workers.dev/api/seasons');
      
      if (!response.ok) {
        throw new Error(`Failed to fetch seasons: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Convert season strings to SeasonWeek objects
      const seasons = (data.seasons || []).map((seasonStr: string) => {
        const [year, week] = seasonStr.split('-').map(Number);
        return {
          year,
          week,
          date: '' // We'll get the date from the poll data
        };
      });
      
      return seasons;
    } catch (error) {
      console.error('Error getting seasons:', error);
      return [];
    }
  }

  async getPollData(year: number, week: number): Promise<{
    rankedTeams: PollData[];
    othersReceivingVotes: PollData[];
  }> {
    try {
      // Use the API instead of parsing SQL
      const response = await fetch(`https://basketbase-workers.bakonydraco1.workers.dev/api/poll/${year}/${week}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch poll data: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Convert API response to expected format (convert snake_case to camelCase)
      const rankedTeams = (data.rankedTeams || []).map((team: any) => ({
        pollUniqueId: team.poll_unique_id,
        pollWeek: team.poll_week,
        pollYear: team.poll_year,
        pollDate: team.poll_date,
        pollRank: team.poll_rank,
        pollPrev: team.poll_prev,
        pollSchool: team.poll_school,
        pollFpv: team.poll_fpv,
        pollPoints: team.poll_points,
        pollTeams: team.poll_teams,
        pollStatus: team.poll_status,
        pollAtp: team.poll_atp,
      }));
      
      const othersReceivingVotes = (data.othersReceivingVotes || []).map((team: any) => ({
        pollUniqueId: team.poll_unique_id,
        pollWeek: team.poll_week,
        pollYear: team.poll_year,
        pollDate: team.poll_date,
        pollRank: team.poll_rank,
        pollPrev: team.poll_prev,
        pollSchool: team.poll_school,
        pollFpv: team.poll_fpv,
        pollPoints: team.poll_points,
        pollTeams: team.poll_teams,
        pollStatus: team.poll_status,
        pollAtp: team.poll_atp,
      }));
      
      return { rankedTeams, othersReceivingVotes };
    } catch (error) {
      console.error('Error getting poll data:', error);
      return { rankedTeams: [], othersReceivingVotes: [] };
    }
  }

  getTeamLogoUrl(teamName: string): string {
    // Convert team name to filename format for cdn.redditcfb.com
    const filename = teamName
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/[^a-z0-9]/g, '');
    
    return `https://cdn.redditcfb.com/60x40/cfb/${filename}.png`;
  }
}

export const databaseService = new DatabaseService();